import { Color } from '@persona-id/design-tokens';
import { UsersCheckIcon } from '@persona-id/design-tokens/icons/24px';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import { MonoSpan } from 'dashboard/components/Typography';
import VerificationStatusSticker from 'dashboard/components/VerificationStatusSticker';
import TableLayout, {
  HelpTextWithButton,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Verification } from 'dashboard/models/verification';
import Spacer from 'design-system/components/Spacer';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

const ResolutionContainer = styled('div')`
  max-width: 400px;
`;

interface VerificationsTableProps {
  verifications: Verification[];
  sortParams: SortParams;
  isLoading: boolean;
  hasUsedFilters: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const VerificationsTable = ({
  verifications,
  sortParams,
  isLoading,
  hasUsedFilters,
  handleSortUpdate,
}: VerificationsTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const verificationRows = verifications.map((verification) => {
    const createdAt = verification.attributes['created-at'];
    const createdAtDisplay = createdAt
      ? DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)
      : '–';

    return (
      <TableLinkTR
        to={`/dashboard/verifications/${verification.id}`}
        data-test={`verifications-table__row--${verification.id}`}
        id={`verifications-table__row--${verification.id}`}
        key={verification.id}
      >
        <TableTD>{VERIFICATION_TYPE_NAMES_SHORT[verification.type]}</TableTD>
        <TableTD>
          <HoverCopyableField copyValue={verification.id} confirmationText='Copied Verification ID'>
            <MonoSpan>{verification.id}</MonoSpan>
          </HoverCopyableField>
        </TableTD>
        <TableTD>{createdAtDisplay}</TableTD>
        <TableTD>
          <ResolutionContainer>
            <VerificationStatusSticker verification={verification} />
          </ResolutionContainer>
        </TableTD>
      </TableLinkTR>
    );
  });

  return (
    <TableLayout
      dataTest='verifications-table'
      isLoading={isLoading}
      showHeader={true}
      isEmpty={verifications.length === 0}
      nullState={
        <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='Verifications-view'>
          <UsersCheckIcon size={64} color={Color.BorderIconSecondary} />
          <Spacer height={3} />
          <div>
            View and verify collected information. Create a verification, follow the steps, then
            find it here.
          </div>
          <Spacer height={5} />
          <a
            href='https://support.withpersona.com/hc/en-us/articles/360055726494-Inquiry-Model-and-other-Key-Concepts'
            target='_blank'
            rel='noreferrer'
          >
            Learn more about verifications
          </a>
        </TableNullState>
      }
      headerTR={
        <>
          <TableTH data-test='verifications-table__type-header'>Type</TableTH>
          <TableTH data-test='verifications-table__verification-id-header'>Verification ID</TableTH>
          <TableTH
            onClick={() => handleColumnHeaderClick('created_at')}
            canSort={true}
            isSorted={sortParams.sort === 'created_at'}
            isDescending={sortParams.shouldSortDescending}
            id='verifications-table__created-at-header'
            data-test='verifications-table__created-at-header'
          >
            {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
          </TableTH>
          <TableTH
            helpText={
              <HelpTextWithButton to='https://docs.withpersona.com/docs/models-lifecycle#verification-statuses'>
                Verification statuses indicate key events in the lifecycle of an individual
                Verification attempt.
              </HelpTextWithButton>
            }
          >
            Status
          </TableTH>
        </>
      }
    >
      {verificationRows}
    </TableLayout>
  );
};

export default VerificationsTable;
